import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Nav,
	NavItem,
	NavLink,
	TabContent,
	TabPane,
	Spinner,
	Alert,
	Button,
	Card,
	CardBody,
	CardHeader,
	Table,
	Badge,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
	fetchResidueTrace,
	fetchResidueMovements,
} from '../../../slices/thunks';
import { invalidateMovements } from '../../../slices/residueTrace/reducer';
import {
	folioLabel,
	formatAt,
	formatKg,
	movementCounterpartLabel,
	movementTypeLabel,
	roleLabel,
} from './traceUi';
import TraceabilityTimeline from './TraceabilityTimeline';
import { buildFilteredGuideTimeline } from './traceTimelineFilter';

const selectResidueTrace = createSelector(
	state => state.ResidueTrace,
	rt => ({
		trace: rt.trace,
		traceLoading: rt.traceLoading,
		traceError: rt.traceError,
		movements: rt.movements,
		movementsLoading: rt.movementsLoading,
		movementsError: rt.movementsError,
		movementsLoaded: rt.movementsLoaded,
	})
);

const DetailSummary = ({ detail }) => {
	if (!detail) return null;
	const name = detail.residue?.name || 'Residuo';
	const cat = detail.residue?.residue_category?.name;
	const fg = folioLabel(detail.guide);
	const kg =
		detail.residue_kilograms != null || detail.residue_collected_kilograms != null
			? formatKg(
				Number(detail.residue_kilograms || 0) + Number(detail.residue_collected_kilograms || 0)
			)
			: detail.amount != null
				? `${detail.amount}`
				: '-';
	return (
		<div className='small'>
			<div className='fw-semibold'>
				{name}
				{cat ? <span className='text-muted fw-normal'> · {cat}</span> : null}
			</div>
			{fg ? <div className='text-muted'>Guía folio: {fg}</div> : null}
			<div className='text-muted'>Cantidad: {kg}</div>
		</div>
	);
};

function lineCardTitle(sub, guide) {
	if (sub?.line?.residue?.name) {
		const res = sub.line.residue.name;
		const cat = sub.line.residue?.residue_category?.name;
		const extra = [cat].filter(Boolean).join(' · ');
		return extra ? `${res} · ${extra}` : res;
	}
	const details = guide?.guide_details;
	if (Array.isArray(details)) {
		const line = details.find(
			gd => String(gd._id) === String(sub.guide_detail_id)
		);
		if (line) {
			const res = line.residue?.name || 'Residuo';
			const cat = line.residue?.residue_category?.name;
			const extra = [cat].filter(Boolean).join(' · ');
			return extra ? `${res} · ${extra}` : res;
		}
	}
	const f = folioLabel(guide);
	return f ? `Línea · guía folio ${f}` : 'Línea de la guía';
}

const ChainGuidesSection = ({ chainGuides, anchorGuideId }) => {
	const rows = (chainGuides || []).filter(Boolean);
	if (rows.length === 0) return null;
	const others = rows.filter(g => !g.anchor && g.guide_id !== anchorGuideId);
	if (others.length === 0 && rows.length <= 1) return null;
	return (
		<>
			<h6 className='text-muted text-uppercase small mt-3'>
				Guías en la cadena ({rows.length})
			</h6>
			{rows.map(g => (
				<Card
					key={g.guide_id}
					className={`mb-2 ${g.anchor ? 'border-primary border-opacity-50' : ''}`}
				>
					<CardBody className='py-2 small'>
						<div className='d-flex justify-content-between align-items-start gap-2'>
							<div>
								<span className='fw-semibold'>
									Folio {folioLabel(g) || '—'}
									{g.anchor ? (
										<Badge color='primary' className='ms-2'>
											Consultada
										</Badge>
									) : null}
								</span>
								<div className='text-muted'>
									{roleLabel(g.role)}
									{g.establishment_name
										? ` · ${g.establishment_name}`
										: ''}
								</div>
								{g.final_destination_name ? (
									<div className='text-muted'>
										DF: {g.final_destination_name}
									</div>
								) : null}
							</div>
						</div>
						{(g.residue_summary || []).length > 0 && (
							<ul className='mb-0 mt-2 ps-3'>
								{g.residue_summary.slice(0, 4).map((r, i) => (
									<li key={`${r.name}-${i}`}>
										{r.name}
										{r.kg != null ? ` · ${formatKg(r.kg)}` : ''}
									</li>
								))}
							</ul>
						)}
					</CardBody>
				</Card>
			))}
		</>
	);
};

const GuideTraceView = ({ data }) => {
	if (!data) return null;
	const err = data.error;
	if (err) {
		return (
			<Alert color='warning' className='mb-0'>
				{err === 'invalid_guide_id'
					? 'No se pudo cargar la trazabilidad de esta guía.'
					: err === 'guide_not_found'
						? 'Guía no encontrada.'
						: 'No se pudo obtener la trazabilidad.'}
			</Alert>
		);
	}
	const guide = data.guide;
	const traces = data.detail_traces || [];
	const chainGuides = data.chain_guides || [];
	const anchorId = data.anchor?.id;
	const mainFolio = folioLabel(guide);

	return (
		<div className='trace-thread'>
			{guide && (
				<Card className='mb-3 border-success border-opacity-25'>
					<CardHeader className='py-2'>Guía</CardHeader>
					<CardBody className='py-2'>
						<div className='small'>
							<div className='fw-semibold'>
								{mainFolio ? (
									<>
										Folio <span className='text-primary'>{mainFolio}</span>
									</>
								) : (
									<span className='text-muted'>Sin folio registrado</span>
								)}
							</div>
						</div>
					</CardBody>
				</Card>
			)}
			<ChainGuidesSection
				chainGuides={chainGuides}
				anchorGuideId={anchorId}
			/>
			<h6 className='text-muted text-uppercase small'>
				Líneas de la guía ({traces.length})
			</h6>
			{traces.length === 0 ? (
				<p className='text-muted small'>Sin detalles en esta guía.</p>
			) : (
				traces.map((sub, idx) => {
					const up = sub.upstream_details || [];
					const down = sub.downstream_details || [];
					const hasGraph = up.length > 0 || down.length > 0;
					return (
						<Card key={sub.guide_detail_id || idx} className='mb-3'>
							<CardHeader className='py-2 d-flex justify-content-between align-items-center'>
								<span>{lineCardTitle(sub, guide)}</span>
								<Badge color='light' className='text-dark'>
									↑{sub.upstream_count ?? up.length} ↓
									{sub.downstream_count ?? down.length}
								</Badge>
							</CardHeader>
							<CardBody className='py-2'>
								{sub.line && (
									<>
										<p className='small text-muted mb-1'>Esta línea</p>
										<div className='mb-3 ps-2 border-start border-3 border-primary'>
											<DetailSummary detail={sub.line} />
										</div>
									</>
								)}
								{!hasGraph && (
									<Alert
										color='light'
										className='small mb-3 py-2 text-muted border'
									>
										Sin vínculos por línea (
										<code>related_guide_detail_ids</code> vacío). El flujo
										logístico (bodega, estados, otras guías) está en{' '}
										<strong>Línea de tiempo</strong> y{' '}
										<strong>Movimientos</strong>.
									</Alert>
								)}
								{hasGraph && (
									<>
										<p className='small text-muted mb-2'>Ascendente</p>
										{up.length === 0 ? (
											<p className='small text-muted'>—</p>
										) : (
											up.map(d => (
												<div
													key={d.id || d._id}
													className='mb-2 ps-2 border-start border-2'
												>
													<DetailSummary detail={d} />
												</div>
											))
										)}
										<p className='small text-muted mb-2 mt-2'>Descendente</p>
										{down.length === 0 ? (
											<p className='small text-muted'>—</p>
										) : (
											down.map(d => (
												<div
													key={d.id || d._id}
													className='mb-2 ps-2 border-start border-2'
												>
													<DetailSummary detail={d} />
												</div>
											))
										)}
									</>
								)}
							</CardBody>
						</Card>
					);
				})
			)}
		</div>
	);
};

const MovementsTable = ({ rows }) => {
	if (!rows || rows.length === 0) {
		return (
			<p className='text-muted mb-0'>
				No hay movimientos registrados para esta guía.
			</p>
		);
	}
	const renderFolio = (folio, fallbackId) => {
		if (folio != null && String(folio).trim() !== '') return String(folio).trim();
		if (fallbackId) return 'Sin folio';
		return '—';
	};
	return (
		<div className='table-responsive' style={{ maxHeight: '420px', overflowY: 'auto' }}>
			<Table size='sm' bordered hover className='mb-0 align-middle'>
				<thead className='table-light sticky-top'>
					<tr>
						<th>Fecha</th>
						<th>Tipo</th>
						<th>Residuo</th>
						<th>Bodega</th>
						<th>Δ kg</th>
						<th>Guía (folio)</th>
						<th>Contraparte</th>
					</tr>
				</thead>
				<tbody>
					{rows.map(row => (
						<tr key={row.id || row._id}>
							<td className='text-nowrap small'>{formatAt(row.creation_date)}</td>
							<td>
								<small>{movementTypeLabel(row.movement_type)}</small>
							</td>
							<td>
								<small>{row.residue_name || '—'}</small>
							</td>
							<td>
								<small>{row.warehouse_name || '—'}</small>
							</td>
							<td>{row.delta_kg != null ? row.delta_kg : '-'}</td>
							<td>
								<small>{renderFolio(row.guide_folio, row.guide_id)}</small>
							</td>
							<td>
								<small>{movementCounterpartLabel(row)}</small>
							</td>
						</tr>
					))}
				</tbody>
			</Table>
		</div>
	);
};

/**
 * @param {{ entityId: string }} props
 */
const TraceabilityPanel = ({ entityId }) => {
	const dispatch = useDispatch();
	const {
		trace,
		traceLoading,
		traceError,
		movements,
		movementsLoading,
		movementsError,
		movementsLoaded,
	} = useSelector(selectResidueTrace);

	const supportsTimeline = !!trace && Array.isArray(trace?.timeline);
	const filteredTimelineCount = useMemo(() => {
		if (!trace || trace.error || !supportsTimeline) return 0;
		return buildFilteredGuideTimeline(trace, entityId).length;
	}, [trace, entityId, supportsTimeline]);

	const hasTimelineEvents = filteredTimelineCount > 0;

	const [activeTab, setActiveTab] = useState('timeline');

	useEffect(() => {
		if (traceLoading) return;
		if (!trace) return;
		if (activeTab === 'timeline' && !supportsTimeline) {
			setActiveTab('hilo');
		}
	}, [trace, traceLoading, supportsTimeline, activeTab]);

	const traceMovements = useMemo(() => {
		const raw = trace?.movements;
		return Array.isArray(raw) ? raw : [];
	}, [trace?.movements]);

	const loadMovements = useCallback(() => {
		dispatch(fetchResidueMovements({ guide_id: entityId }));
	}, [dispatch, entityId]);

	useEffect(() => {
		if (activeTab !== 'movimientos') return;
		if (traceMovements.length > 0) return;
		if (movementsLoaded || movementsLoading) return;
		loadMovements();
	}, [
		activeTab,
		traceMovements.length,
		movementsLoaded,
		movementsLoading,
		loadMovements,
	]);

	const movementRows = useMemo(() => {
		if (traceMovements.length > 0) return traceMovements;
		return movements || [];
	}, [traceMovements, movements]);

	const retryTrace = () => {
		dispatch(
			fetchResidueTrace({
				entityId,
				max_depth: 10,
				include_movements: true,
			})
		);
	};

	const retryMovements = () => {
		dispatch(invalidateMovements());
		loadMovements();
	};

	const timelineTabDisabled = !traceLoading && !!trace && !supportsTimeline;

	const renderHiloTab = () => (
		<>
			<GuideTraceView data={trace} />
			<p className='text-muted small mt-3 mb-0'>
				El hilo por línea usa vínculos entre detalles de guía. La cadena
				completa (bodega, estados, otras guías) está en{' '}
				<strong>Línea de tiempo</strong> y <strong>Movimientos</strong>.
			</p>
		</>
	);

	return (
		<div>
			<Nav tabs className='nav-tabs-custom'>
				<NavItem>
					<NavLink
						className={activeTab === 'timeline' ? 'active' : ''}
						style={{
							cursor: timelineTabDisabled ? 'not-allowed' : 'pointer',
							opacity: timelineTabDisabled ? 0.5 : 1,
						}}
						onClick={() => {
							if (timelineTabDisabled) return;
							setActiveTab('timeline');
						}}
					>
						Línea de tiempo
					</NavLink>
				</NavItem>
				<NavItem>
					<NavLink
						className={activeTab === 'hilo' ? 'active' : ''}
						style={{ cursor: 'pointer' }}
						onClick={() => setActiveTab('hilo')}
					>
						Hilo
					</NavLink>
				</NavItem>
				<NavItem>
					<NavLink
						className={activeTab === 'movimientos' ? 'active' : ''}
						style={{ cursor: 'pointer' }}
						onClick={() => setActiveTab('movimientos')}
					>
						Movimientos
					</NavLink>
				</NavItem>
			</Nav>
			<TabContent activeTab={activeTab} className='pt-3'>
				<TabPane tabId='timeline'>
					{traceLoading && (
						<div className='text-center py-4'>
							<Spinner color='primary' />
							<p className='text-muted small mt-2 mb-0'>
								Cargando línea de tiempo…
							</p>
						</div>
					)}
					{traceError && !traceLoading && (
						<Alert color='danger'>
							{traceError}
							<div className='mt-2'>
								<Button color='primary' size='sm' outline onClick={retryTrace}>
									Reintentar
								</Button>
							</div>
						</Alert>
					)}
					{!traceLoading && !traceError && supportsTimeline && (
						<>
							{!hasTimelineEvents ? (
								<p className='text-muted mb-0'>
									Esta guía aún no tiene eventos en la línea de tiempo.
								</p>
							) : (
								<TraceabilityTimeline trace={trace} entityId={entityId} />
							)}
						</>
					)}
					{!traceLoading && !traceError && trace && !supportsTimeline && (
						<Alert color='info' variant='light' className='mb-0'>
							La línea de tiempo no está disponible en este entorno. Usá las
							pestañas <strong>Hilo</strong> y <strong>Movimientos</strong>.
						</Alert>
					)}
				</TabPane>
				<TabPane tabId='hilo'>
					{traceLoading && (
						<div className='text-center py-4'>
							<Spinner color='primary' />
							<p className='text-muted small mt-2 mb-0'>Cargando trazabilidad…</p>
						</div>
					)}
					{traceError && !traceLoading && (
						<Alert color='danger'>
							{traceError}
							<div className='mt-2'>
								<Button color='primary' size='sm' outline onClick={retryTrace}>
									Reintentar
								</Button>
							</div>
						</Alert>
					)}
					{!traceLoading && !traceError && trace && renderHiloTab()}
					{!traceLoading && !traceError && !trace && (
						<p className='text-muted mb-0'>
							No hay información de trazabilidad para este registro.
						</p>
					)}
				</TabPane>
				<TabPane tabId='movimientos'>
					{movementsLoading && movementRows.length === 0 && (
						<div className='text-center py-4'>
							<Spinner color='primary' />
							<p className='text-muted small mt-2 mb-0'>Cargando movimientos…</p>
						</div>
					)}
					{movementsError && !movementsLoading && movementRows.length === 0 && (
						<Alert color='danger'>
							{movementsError}
							<div className='mt-2'>
								<Button color='primary' size='sm' outline onClick={retryMovements}>
									Reintentar
								</Button>
							</div>
						</Alert>
					)}
					{movementRows.length > 0 && (
						<>
							<MovementsTable rows={movementRows} />
							<p className='text-muted small mt-2 mb-0'>
								{traceMovements.length > 0
									? 'Mismos movimientos que alimentan la línea de tiempo.'
									: null}
							</p>
						</>
					)}
					{!movementsLoading &&
						!movementsError &&
						movementRows.length === 0 &&
						!traceLoading && (
							<p className='text-muted mb-0'>
								No hay movimientos de bodega para esta guía.
							</p>
						)}
				</TabPane>
			</TabContent>
		</div>
	);
};

export default TraceabilityPanel;
